"use client";
import { api } from "@/utils/api";
import { Button } from "./ui/button";
import { toast } from "react-toastify";
import { handleTRPCError } from "@/utils/errorHandling";
import { ConfirmDestructiveActionDialog } from "./ConfirmDestructiveActionDialog";
import { Loader2, RotateCcw, Trash2, Unlock, Lock, Edit, XCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect } from "react";
import { RawCommandDialog } from "./RawCommandDialog";
import { type device_config, type customers, type smb_images } from "@prisma/client";
import { RawMessageDialog } from "./RawMessageDialog";
import Link from "next/link";
import { TooltipWrapper } from "./Tooltip";
import { type DeviceSmbUpdateHistory, type GetAllNotesOutput, type GetAllUserCustomersOutput, type SmbConfigProfile } from "@/types/types";
import { useAtom } from "jotai";
import { selectedCustomerAtom } from "@/components/CustomerSelector";
import { userCanManageRoleForCustomer, userIsDevAdminForCustomer, userIsSuperAdmin, userIsUniversalDevAdmin, userIsUniversalHeadAdmin } from "@/utils/accessControl";
import { type preset } from "@prisma/client";
import { signIn, signOut, useSession } from "@/lib/auth-client";

export const CancelBackButton = ({ variant, className, size }: { variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link" | null | undefined, className?: string | undefined, size?: "default" | "sm" | "lg" | "icon" | null | undefined }) => {
    const router = useRouter();
    return (
        <Button variant={variant} className={className} size={size} onClick={() => router.back()}>Cancel
        </Button>
    );
}
export const SignOutButton = ({ className, size }: { className: string, size: "default" | "sm" | "lg" | "icon" | null | undefined }) => {
    return (
        <Button className={className} size={size} onClick={() => signOut()}>Sign out</Button>
    );
}

export const SignInButton = ({ className, size }: { className: string, size: "default" | "sm" | "lg" | "icon" | null | undefined }) => {
    return (
        <Button className={className} size={size} onClick={() => signIn()}>Sign in</Button>
    );
}

export const RemoveFromGroupButton = ({ deviceID, groupID }: { deviceID: string, groupID: string }) => {
    const utils = api.useUtils();
    const { mutateAsync, isPending, error: mutateError } = api.group.removeMember.useMutation({
        onSuccess: async () => {
            await utils.group.getGroupsForMember.invalidate();
        },
    });

    useEffect(() => {
        if (mutateError?.data?.code === "BAD_REQUEST" && !mutateError?.data?.zodError) {
            toast.error(mutateError.message);
        }
    }, [mutateError]);

    const handleRemove = () => void toast.promise(
        mutateAsync({ device_cuid: deviceID, group_cuid: groupID }),
        {
            success: 'Device removed from group successfully',
        }
    );

    return (
        <ConfirmDestructiveActionDialog text="This device will be removed from this group." action={handleRemove}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const CommandButtons = ({ id, disabled }: { id: string, disabled: boolean }) => {
    // Get user role from session
    const { data: session } = useSession();
    const [selectedCustomer] = useAtom(selectedCustomerAtom);
    if (!session) return null;
    if (!selectedCustomer) return null;
    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-3">
                <ResetMediaPlayerCommandButton device_cuid={id} customer_cuid={selectedCustomer} disabled={disabled} />
                <ResetModemCommandButton device_cuid={id} customer_cuid={selectedCustomer} disabled={disabled} />
                <ResetLCDDriverCommandButton device_cuid={id} customer_cuid={selectedCustomer} disabled={disabled} />
                <ResetHostCommandButton device_cuid={id} customer_cuid={selectedCustomer} disabled={disabled} />
                <ResetSMBCommandButton device_cuid={id} customer_cuid={selectedCustomer} disabled={disabled} />
            </div>
            <div className="flex flex-wrap gap-3">
                {userIsDevAdminForCustomer(session, selectedCustomer) ? <RawCommandDialog device_cuid={id} customer_cuid={selectedCustomer} disabled={disabled} /> : null}
                {userIsDevAdminForCustomer(session, selectedCustomer) ? <RawMessageDialog device_cuid={id} customer_cuid={selectedCustomer} disabled={disabled} /> : null}
            </div>
        </div>
    )
}

export const ResetMediaPlayerCommandButton = ({ device_cuid, customer_cuid, disabled }: { device_cuid: string, customer_cuid: string, disabled: boolean }) => {
    const { mutateAsync, isPending } = api.command.send.useMutation({
        onError: async (error) => {
            handleTRPCError(error, "Failed to send reset command to the device");
        },
        onSuccess: async () => {
            toast.success("Media player reset command sent successfully");
        }
    });
    const command = {
        message_type: "command",
        command: "SMB_Reset",
        target: "Mediaplayer"
    }
    const handleClick = () => mutateAsync({ device_cuid, customer_cuid, command: JSON.stringify(command) })

    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to reset the media player?" action={handleClick}>
            <Button disabled={disabled || isPending} size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                <span>
                    Reset Mediaplayer
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}
export const ResetModemCommandButton = ({ device_cuid, customer_cuid, disabled }: { device_cuid: string, customer_cuid: string, disabled: boolean }) => {
    const { mutateAsync, isPending } = api.command.send.useMutation({
        onError: async (error) => {
            handleTRPCError(error, "Failed to send reset command to the device");
        },
        onSuccess: async () => {
            toast.success("Modem reset command sent successfully");
        }
    });
    const command = {
        message_type: "command",
        command: "SMB_Reset",
        target: "Modem"
    }
    const handleClick = () => mutateAsync({ device_cuid, customer_cuid, command: JSON.stringify(command) })
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to reset the modem?" action={handleClick}>
            <Button disabled={disabled || isPending} size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                <span>
                    Reset Modem
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}
export const ResetLCDDriverCommandButton = ({ device_cuid, customer_cuid, disabled }: { device_cuid: string, customer_cuid: string, disabled: boolean }) => {
    const { mutateAsync, isPending } = api.command.send.useMutation({
        onError: async (error) => {
            handleTRPCError(error, "Failed to send reset command to the device");
        },
        onSuccess: async () => {
            toast.success("LCD driver reset command sent successfully");
        }
    });
    const command = {
        message_type: "command",
        command: "SMB_Reset",
        target: "LCDDriver"
    }
    const handleClick = () => mutateAsync({ device_cuid, customer_cuid, command: JSON.stringify(command) })
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to reset the LCD driver?" action={handleClick}>
            <Button disabled={disabled || isPending} size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                <span>
                    Reset LCD Driver
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}
export const ResetHostCommandButton = ({ device_cuid, customer_cuid, disabled }: { device_cuid: string, customer_cuid: string, disabled: boolean }) => {
    const { mutateAsync, isPending } = api.command.send.useMutation({
        onError: async (error) => {
            handleTRPCError(error, "Failed to send reset command to the device");
        },
        onSuccess: async () => {
            toast.success("Host reset command sent successfully");
        }
    });
    const command = {
        message_type: "command",
        command: "SMB_Reset",
        target: "Host"
    }
    const handleClick = () => mutateAsync({ device_cuid, customer_cuid, command: JSON.stringify(command) })
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to reset the host system?" action={handleClick}>
            <Button disabled={disabled || isPending} size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                <span>
                    Reset Host
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}
export const ResetSMBCommandButton = ({ device_cuid, customer_cuid, disabled }: { device_cuid: string, customer_cuid: string, disabled: boolean }) => {
    const { mutateAsync, isPending } = api.command.send.useMutation({
        onError: async (error) => {
            handleTRPCError(error, "Failed to send reset command to the device");
        },
        onSuccess: async () => {
            toast.success("SMB reset command sent successfully");
        }
    });
    const command = {
        message_type: "command",
        command: "SMB_Reset",
        target: "SMB"
    }
    const handleClick = () => mutateAsync({ device_cuid, customer_cuid, command: JSON.stringify(command) })
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to reset the SMB?" action={handleClick}>
            <Button disabled={disabled || isPending} size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                <span>
                    Reset SMB
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}

export const LockCommandButton = ({ device_cuid, customer_cuid, disabled }: { device_cuid: string, customer_cuid: string, disabled: boolean }) => {
    const { mutateAsync, isPending } = api.command.send.useMutation({
        onError: async (error) => {
            handleTRPCError(error, "Failed to send lock command to the device");
        },
        onSuccess: async () => {
            toast.success("Lock command sent successfully");
        }
    });
    const command = {
        message_type: "command",
        command: "SMB_Set",
        target: "electric_lock_state",
        value: "close"
    }
    const handleClick = () => mutateAsync({ device_cuid, customer_cuid, command: JSON.stringify(command) })
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to lock this device?" action={handleClick}>
            <Button disabled={disabled || isPending} size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                <span>
                    Lock
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}

export const UnlockCommandButton = ({ device_cuid, customer_cuid, disabled }: { device_cuid: string, customer_cuid: string, disabled: boolean }) => {
    const { mutateAsync, isPending } = api.command.send.useMutation({
        onError: async (error) => {
            handleTRPCError(error, "Failed to send unlock command to the device");
        },
        onSuccess: async () => {
            toast.success("Unlock command sent successfully");
        }
    });
    const command = {
        message_type: "command",
        command: "SMB_Set",
        target: "electric_lock_state",
        value: "open"
    }
    const handleClick = () => mutateAsync({ device_cuid, customer_cuid, command: JSON.stringify(command) })
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to unlock this device?" action={handleClick}>
            <Button disabled={disabled || isPending} size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlock className="mr-2 h-4 w-4" />}
                <span>
                    Unlock
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}

export const IgnoreAlertButton = ({ alert_id, customer_cuid, device_cuid }: { alert_id: number, customer_cuid: string, device_cuid: string }) => {
    const { alert } = api.useUtils();
    const { mutateAsync, isPending } = api.alert.ignoreOne.useMutation({
        onSuccess: async () => {
            await alert.getAllForCustomer.invalidate([customer_cuid]);
            await alert.getAllForGroup.invalidate();
            await alert.getAllActiveForDevice.invalidate({ customer_cuid, device_cuid });
        },
    });

    const handleClick = () => void toast.promise(
        mutateAsync({ alert_id, customer_cuid }),
        {
            success: 'Alert ignored successfully',
            error: 'Failed to ignore alert'
        }
    );
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to ignore this alert?" action={handleClick}>
            <div>
                <TooltipWrapper content="Ignore alert" side="top">
                    <Button variant="ghost" size="icon">
                        {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                    </Button>
                </TooltipWrapper>
            </div>
        </ConfirmDestructiveActionDialog>

    )
}

export const IgnoreAllAlertsButton = ({ customers, groupCuid }: { customers: string[], groupCuid?: string }) => {
    const { alert } = api.useUtils();
    const customerMutation = api.alert.ignoreAll.useMutation({
        onSuccess: async () => {
            await Promise.all([
                alert.getAllForCustomer.invalidate(customers),
                alert.getAllForGroup.invalidate(),
            ]);
        },
    });
    const groupMutation = api.alert.ignoreAllForGroup.useMutation({
        onSuccess: async () => {
            await Promise.all([
                alert.getAllForCustomer.invalidate(customers),
                alert.getAllForGroup.invalidate({ group_cuid: groupCuid ?? '' }),
            ]);
        },
    });
    const isPending = customerMutation.isPending || groupMutation.isPending;

    const handleClick = () => void toast.promise(
        groupCuid
            ? groupMutation.mutateAsync({ group_cuid: groupCuid })
            : customerMutation.mutateAsync(customers),
        {
            success: 'Alerts ignored successfully',
            error: 'Failed to ignore alerts'
        }
    );
    return (
        <ConfirmDestructiveActionDialog text="Are you sure you want to ignore all the alerts?" action={handleClick}>
            <Button disabled={isPending} variant="destructive">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <span>Ignore all</span>}
            </Button>
        </ConfirmDestructiveActionDialog>

    )
}

export const RemoveNoteButton = ({ note }: { note: GetAllNotesOutput }) => {
    const { note: noteQueries } = api.useUtils();
    const { isPending, mutateAsync } = api.note.delete.useMutation({
        onSuccess: async () => {
            await noteQueries.getAll.invalidate({ device_cuid: note.device_cuid, customer_cuid: note.devices.customer_cuid });
        },
    });
    const handleClick = () => {
        void toast.promise(
            mutateAsync({ id: note.id, customer_cuid: note.devices.customer_cuid }),
            {
                success: 'Note removed successfully',
                error: 'Failed to remove note'
            }
        )
    }
    return (
        <ConfirmDestructiveActionDialog text="This note will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const RemoveScheduleButton = ({ id, device_cuid, customer_cuid }: { id: number, device_cuid: string, customer_cuid: string }) => {
    const utils = api.useUtils();
    const { mutate: mutateCommand, isPending: commandIsPending } = api.command.sendSchedules.useMutation({
        onError: async (error) => {
            toast.error(error.message);
            await utils.device.getErrors.invalidate({ device_cuid, customer_cuid });
            await utils.schedule.getAll.invalidate({ device_cuid, customer_cuid });
            await utils.device.getSMBResponses.invalidate({ device_cuid, customer_cuid });
        },
        onSuccess: async () => {
            toast.success("Updated schedules sent to the device")
            await utils.device.getErrors.invalidate({ device_cuid, customer_cuid });
            await utils.schedule.getAll.invalidate({ device_cuid, customer_cuid });
            await utils.device.getSMBResponses.invalidate({ device_cuid, customer_cuid });
        },
    });
    const { isPending, mutate } = api.schedule.delete.useMutation({
        onSuccess: () => {
            toast.success("Schedule deleted")
            mutateCommand({
                device_cuid, customer_cuid
            })
        },
    });
    const handleClick = () => {
        mutate({ id, customer_cuid })
    }
    return (
        <ConfirmDestructiveActionDialog text="This schedule will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending || commandIsPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const RemoveCommandScheduleButton = ({ id, device_cuid, customer_cuid }: { id: number, device_cuid: string, customer_cuid: string }) => {
    const utils = api.useUtils();
    const { mutate: mutateCommand, isPending: commandIsPending } = api.command.sendCommandSchedules.useMutation({
        onError: async (error) => {
            toast.error(error.message);
            await utils.device.getErrors.invalidate({ device_cuid, customer_cuid });
            await utils.commandSchedule.getAll.invalidate({ device_cuid, customer_cuid });
            await utils.device.getSMBResponses.invalidate({ device_cuid, customer_cuid });
        },
        onSuccess: async () => {
            toast.success("Updated schedules sent to the device")
            await utils.device.getErrors.invalidate({ device_cuid, customer_cuid });
            await utils.commandSchedule.getAll.invalidate({ device_cuid, customer_cuid });
            await utils.device.getSMBResponses.invalidate({ device_cuid, customer_cuid });
        },
    });
    const { isPending, mutate } = api.commandSchedule.delete.useMutation({
        onSuccess: () => {
            toast.success("Schedule deleted")
            mutateCommand({
                device_cuid, customer_cuid
            })
        },
    });
    const handleClick = () => {
        mutate({ id, customer_cuid })
    }
    return (
        <ConfirmDestructiveActionDialog text="This schedule will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending || commandIsPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const RemoveCustomerConfigButton = ({ config_id, customer_cuid }: { config_id: number, customer_cuid: string }) => {
    const { customerConfig } = api.useUtils();
    const { isPending, mutate } = api.customerConfig.delete.useMutation({
        onSuccess: async () => {
            toast.success("Schedule deleted")
            await customerConfig.getAllForCustomer.invalidate(customer_cuid);
        },
    });
    const handleClick = () => {
        mutate({ config_id, customer_cuid })
    }
    return (
        <ConfirmDestructiveActionDialog text="This configuration will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const RemoveSystemConfigButton = ({ config_id }: { config_id: number }) => {
    const { systemConfig } = api.useUtils();
    const { isPending, mutate } = api.systemConfig.delete.useMutation({
        onSuccess: async () => {
            toast.success("Schedule deleted")
            await systemConfig.getAll.invalidate();
        },
    });
    const handleClick = () => {
        mutate({ config_id })
    }
    return (
        <ConfirmDestructiveActionDialog text="This configuration will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const ConfigureSMBButton = ({ id }: { id: string }) => {
    return (
        <Button variant={'destructive'} asChild>
            <Link href={`/device/${id}/smbconfig`}>
                <Edit className="mr-2 h-4 w-4" />
                <span>
                    Configure SMB
                </span>
            </Link>
        </Button>
    )
}

export const SystemConfigButton = () => {
    return (
        <Button variant={'destructive'} asChild>
            <Link href={`/admin/system_config`}>
                <Edit className="mr-2 h-4 w-4" />
                <span>
                    Change system configuration
                </span>
            </Link>
        </Button>
    )
}

export const CustomerConfigButton = () => {
    return (
        <Button variant={'destructive'} asChild>
            <Link href={`/admin/customer_config`}>
                <Edit className="mr-2 h-4 w-4" />
                <span>
                    Change system configuration
                </span>
            </Link>
        </Button>
    )
}

export const ScheduleBacklightButton = ({ id }: { id: string }) => {
    return (
        <Button asChild>
            <Link href={`/device/${id}/schedule`}>
                <Edit className="mr-2 h-4 w-4" />
                <span>
                    Schedule backlight
                </span>
            </Link>
        </Button>
    )
}

export const DeleteUserButton = ({ user }: { user: GetAllUserCustomersOutput }) => {
    const { data: session } = useSession();
    const { user: userRequests } = api.useUtils();
    const { isPending, mutateAsync } = api.user.delete.useMutation({
        onSuccess: async () => {
            await Promise.all([
                userRequests.getAllUserCustomers.invalidate(user.customer_cuid),
                userRequests.getAllUsers.invalidate(),
            ]);
        },
    });
    if (!session || !userCanManageRoleForCustomer(session, user.customer_cuid, user.role)) return null;
    const handleClick = () => {
        void toast.promise(
            mutateAsync({
                user_id: user.user_id,
                customer_cuid: user.customer_cuid
            }),
            {
                success: 'User deleted successfully',
                error: 'Failed to delete user'
            }
        )
    }
    return (
        <ConfirmDestructiveActionDialog text="This user's access to this customer environment will be removed. If this is their last environment, the user account will also be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const SoftDeleteDeviceButton = ({ device_cuid, customer_cuid }: { device_cuid: string, customer_cuid: string }) => {
    const { device: deviceReguests } = api.useUtils();
    const { data: session } = useSession();
    const { isPending, mutateAsync } = api.device.delete.useMutation({
        onSuccess: async () => {
            await deviceReguests.getAllForCustomer.invalidate([customer_cuid]);
        },
    });
    const handleClick = () => {
        void toast.promise(
            mutateAsync({
                device_cuid,
                customer_cuid
            }),
            {
                success: 'Device soft deleted successfully',
                error: 'Failed to soft delete device'
            }
        )
    }
    if (!userIsUniversalHeadAdmin(session)) return null;
    return (
        <ConfirmDestructiveActionDialog text="All data related to this device will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Soft delete
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}
export const HardDeleteDeviceButton = ({ device_cuid, customer_cuid }: { device_cuid: string, customer_cuid: string }) => {
    const { device: deviceReguests } = api.useUtils();
    const { data: session } = useSession();
    const { isPending, mutateAsync } = api.device.hardDelete.useMutation({
        onSuccess: async () => {
            await deviceReguests.getAllForCustomer.invalidate([customer_cuid]);
        },
    });
    const handleClick = () => {
        void toast.promise(
            mutateAsync({
                device_cuid,
                customer_cuid
            }),
            {
                success: 'Device hard deleted successfully',
                error: 'Failed to hard delete device'
            }
        )
    }
    if (!userIsUniversalHeadAdmin(session)) return null;
    return (
        <ConfirmDestructiveActionDialog text="All device data and device config will be removed" action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Hard delete
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const DeleteCustomerButton = ({ customer }: { customer: customers }) => {
    const { customer: customerReguests } = api.useUtils();
    const { data: session } = useSession();
    const { isPending, mutateAsync, error: mutateError } = api.customer.delete.useMutation({
        onSuccess: async () => {
            await customerReguests.getAllForUser.invalidate();
            await customerReguests.getAll.invalidate()
        },
    });

    useEffect(() => {
        if (mutateError?.data?.code === "BAD_REQUEST" && !mutateError?.data?.zodError) {
            toast.error(mutateError.message);
        }
    }, [mutateError]);

    const handleClick = () => {
        void toast.promise(
            mutateAsync(customer.id),
            {
                success: 'Customer deleted successfully',
            }
        )
    }
    if (!userIsUniversalHeadAdmin(session)) return null;
    if (customer.internal && !userIsSuperAdmin(session)) return null;
    return (
        <ConfirmDestructiveActionDialog text="This customer along with ALL THE DATA RELATED TO THIS CUSTOMER will be deleted permanently. Are you sure you want to do this?" action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Delete
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const DeleteSmbImageButton = ({ image }: { image: smb_images }) => {
    const { SMBImage } = api.useUtils();
    const { data: session } = useSession();
    const { isPending, mutateAsync, error: mutateError } = api.SMBImage.delete.useMutation({
        onSuccess: async () => {
            await SMBImage.getAll.invalidate()
            await SMBImage.getUpdateHistory.invalidate()
        },
    });

    useEffect(() => {
        if (mutateError?.data?.code === "BAD_REQUEST" && !mutateError?.data?.zodError) {
            toast.error(mutateError.message);
        }
    }, [mutateError]);

    const handleClick = () => {
        void toast.promise(
            mutateAsync(image.id),
            {
                success: 'SMB image deleted successfully',
            }
        )
    }
    if (!userIsUniversalDevAdmin(session)) return null;
    return (
        <ConfirmDestructiveActionDialog text="This SMB image and all device update history with it will be deleted permanently. Are you sure you want to do this?" action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Delete
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const DeleteSmbProfileButton = ({ profile }: { profile: SmbConfigProfile }) => {
    const { smbConfigProfile } = api.useUtils();
    const { data: session } = useSession();
    const { isPending, mutateAsync, error: mutateError } = api.smbConfigProfile.delete.useMutation({
        onSuccess: async () => {
            await smbConfigProfile.getAll.invalidate()
        },
    });

    useEffect(() => {
        if (mutateError?.data?.code === "BAD_REQUEST" && !mutateError?.data?.zodError) {
            toast.error(mutateError.message);
        }
    }, [mutateError]);

    const handleClick = () => {
        void toast.promise(
            mutateAsync(profile.id),
            {
                success: 'SMB profile deleted successfully',
            }
        )
    }
    if (!userIsUniversalDevAdmin(session)) return null;
    return (
        <ConfirmDestructiveActionDialog text="This SMB config profile will be deleted permanently. Are you sure you want to do this?" action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Delete
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const RetrySmbUpdateButton = ({ update }: { update: DeviceSmbUpdateHistory }) => {
    const { SMBImage } = api.useUtils();
    const { data: session } = useSession();
    const { isPending, mutateAsync, error: mutateError } = api.SMBImage.retryUpdate.useMutation({
        onSuccess: async () => {
            await SMBImage.getUpdateHistory.invalidate(update.device_cuid);
        },
    });

    const { isPending: sendIsPending, mutateAsync: sendMutateAsync, error: sendError } = api.command.sendSmbUpdate.useMutation({
        onSuccess: async () => {
            await SMBImage.getUpdateHistory.invalidate(update.device_cuid);
            toast.success("SMB update sent successfully");
        },
    });
    useEffect(() => {
        if (mutateError?.data?.code === "BAD_REQUEST" && !mutateError?.data?.zodError) {
            toast.error(mutateError.message);
        }
        if (mutateError?.data?.code === "INTERNAL_SERVER_ERROR" && !mutateError?.data?.zodError) {
            toast.error(mutateError.message);
        }
        if (sendError?.data?.code === "NOT_FOUND" && !sendError?.data?.zodError) {
            toast.error(sendError.message);
        }
        if (sendError?.data?.code === "INTERNAL_SERVER_ERROR" && !sendError?.data?.zodError) {
            toast.error(sendError.message);
        }
    }, [mutateError, sendError]);

    const handleClick = async () => {
        await mutateAsync(update.id);
        await sendMutateAsync({ update_id: update.id, device_cuid: update.device_cuid });


    }
    if (!userIsUniversalDevAdmin(session)) return null;
    return (
        <Button size="sm" onClick={handleClick}>
            {isPending || sendIsPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
            <span>
                Retry update
            </span>
        </Button>
    )
}

export const RemoveGroupButton = ({ group_cuid, customer_cuid }: { group_cuid: string, customer_cuid: string }) => {
    const utils = api.useUtils();
    const router = useRouter();
    const { isPending, mutateAsync } = api.group.delete.useMutation({
        onSuccess: async () => {
            await utils.group.getAllForCustomer.invalidate([customer_cuid]);
            await utils.group.getGroupsForMember.invalidate();
            router.push('/grouplist')
        },
    });
    const handleClick = () => {
        void toast.promise(
            mutateAsync(group_cuid),
            {
                success: 'Group removed successfully',
                error: 'Failed to remove group'
            }
        )
    }
    return (
        <ConfirmDestructiveActionDialog text="This group will be deleted." action={handleClick}>
            <Button variant="destructive">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove group
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const DeclineButton = ({ id }: { id: string }) => {
    const { mutateAsync, isPending } = api.deviceConfig.deleteUnknown.useMutation({
        onSuccess: async () => {
            await utils.deviceConfig.getAllUnknown.invalidate();
        },
    });
    const utils = api.useUtils();
    const handleDecline = () => void toast.promise(
        mutateAsync(id),
        {
            success: 'Device deleted successfully',
            error: 'Operation failed'
        }
    );
    return <ConfirmDestructiveActionDialog text="Access will be denied for this device." action={handleDecline}>
        <Button variant="destructive" size="sm">
            {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
            <span>
                Delete
            </span>
        </Button>
    </ConfirmDestructiveActionDialog>
}

export const DeletePresetButton = ({ preset }: { preset: preset }) => {
    const { preset: presetRequests } = api.useUtils();
    const { isPending, mutateAsync } = api.preset.delete.useMutation({
        onSuccess: async () => {
            await presetRequests.getForCustomer.invalidate(preset.customer_cuid);
        },
    });
    const handleClick = () => {
        void toast.promise(
            mutateAsync(preset.id),
            {
                success: 'Preset deleted successfully',
                error: 'Failed to delete preset'
            }
        )
    }
    return (
        <ConfirmDestructiveActionDialog text="This preset will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Delete
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}

export const DeleteDeviceConfigButton = ({ config }: { config: device_config }) => {
    const { deviceConfig: configRequests } = api.useUtils();
    const { isPending, mutateAsync } = api.deviceConfig.delete.useMutation({
        onSuccess: async () => {
            await configRequests.getForCustomer.invalidate(config.customer_cuid);
        },
    });
    const handleClick = () => {
        void toast.promise(
            mutateAsync({ config_id: config.id, customer_cuid: config.customer_cuid }),
            {
                success: 'Device config deleted successfully',
                error: 'Failed to delete device config'
            }
        )
    }
    return (
        <ConfirmDestructiveActionDialog text="This device config will be deleted." action={handleClick}>
            <Button variant="destructive" size="sm">
                {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                <span>
                    Remove
                </span>
            </Button>
        </ConfirmDestructiveActionDialog>
    )
}
